import { useEffect, useRef } from 'react';
import classNames from 'classnames';
import ArrowDown from 'components/shared/arrow-down';
import ArrowUp from 'components/shared/arrow-up';
import { FILTER_ACTION_TYPES } from 'hooks/filterGlobalState/filterReducer';
import useIsMobile from 'hooks/utils/useIsMobile';
import CustomCheckbox from 'components/shared/customCheckbox';
import styles from './filterDropdown.module.scss';

const FilterDropdown = ({ className, filterName, options, isOptionActive, useFilterObject }) => {
  const { filterState, updateFilterState, addFilter, removeFilter } = useFilterObject;
  const isOpen = filterState.openedDropdown === filterName;
  const menuDropdownRef = useRef(null);
  const isMobile = useIsMobile();

  const openDropdown = () => {
    updateFilterState({ type: FILTER_ACTION_TYPES.OPENED_DROPDOWN.SET, value: filterName });
  };

  const closeDropDown = () => {
    updateFilterState({ type: FILTER_ACTION_TYPES.OPENED_DROPDOWN.CLEAR });
  };

  useEffect(() => {
    if (!isOpen) return null;
    const handleClick = e => {
      if (isMobile) return;
      if (
        menuDropdownRef?.current &&
        !menuDropdownRef.current.contains(e.target) &&
        e.target.getAttribute('data-role') !== 'optionSelectorInner'
      ) {
        closeDropDown();
      }
    };
    window.addEventListener('click', handleClick);

    return () => window.removeEventListener('click', handleClick);
  }, [isOpen, isMobile]);

  const isOptionSelected = option =>
    filterState.selectedFilters?.some(
      selectedOption => selectedOption.id === option.id && selectedOption.value === option.value
    );

  const selectHandler = option => {
    if (isOptionSelected(option)) {
      removeFilter(option);
    } else {
      addFilter(option);
    }
  };

  return (
    <div
      className={classNames(className, styles.filterSelectWrapper, {
        [styles.foldedOutFilterCard]: isOpen,
      })}
    >
      <div
        role="listbox"
        tabIndex={0}
        className={styles.filterSelectTopCard}
        onClick={() => openDropdown()}
        onKeyDown={() => openDropdown()}
      >
        <h3 className={styles.filterName}>{filterName}</h3>
        <button className={styles.openClose} type="button">
          {!isOpen ? (
            <ArrowDown width="1.4rem" height="1.4rem" />
          ) : (
            <ArrowUp width="1.4rem" height="1.4rem" />
          )}
        </button>
      </div>
      {isOpen && (
        <div className={styles.filterSelectBottomCard} ref={menuDropdownRef}>
          {options.map(option => (
            <div
              role="option"
              aria-selected={
                filterState.desiredFilters?.find(selectedOption => selectedOption === option) ===
                option
              }
              tabIndex={0}
              key={`${option.type}-${option.value}`}
              className={classNames(styles.option, {
                [styles.disabledOption]: !isOptionActive(option),
              })}
              onClick={() => selectHandler(option)}
              onKeyDown={() => selectHandler(option)}
            >
              <CustomCheckbox checked={isOptionSelected(option)} />
              <span className={styles.optionValue}>{option.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FilterDropdown;
